import styled from '@emotion/styled';
import { useMemo } from 'react';
import { UserType } from '@diritto/common/account/constants/user.constant';
import { UserMeDto } from '@diritto/common/account/dtos/user-me.dto';
import { ContentStatus } from '@diritto/common/content/constants';
import { ContentContestDto } from '@diritto/common/content/dtos/content-contest.dto';
import { ContestStage } from '@diritto/common/content/types/content-contest-info-types';
import { ContentPublicRelayDto } from '@diritto/common/relay/dtos/content-relay.dtos';
import { Color } from '@diritto/common/style/color';
import { FontWeight, Typography } from '@diritto/common/style/typography';
import { toMap } from '@diritto/common/utils/array';
import { convertToDotString } from '@diritto/common/utils/date';
import { numberComma } from '@diritto/common/utils/string';
import { useContestStage } from 'state-hooks/contests/useStageValidation';
import { useGetMyContestContentsQuery } from 'queries/contests/useGetMyContestContentsQuery';
import { ContestContentMoreMenu } from 'app-components/studio/contests/ContestContentMoreMenu';
import {
  PinnedTh,
  Table,
  TableContainer,
  TableWrapper,
  Tbody,
  Td,
  Th,
  Thead,
  ThInTBody,
  Tr,
} from 'app-components/studio/myContents/table';
import { useTableHook } from 'app-components/studio/myContents/useTableHook';
import { isPC } from 'constants/layout';
import { ZIndex } from 'constants/zIndex';
import { ArrowHorizontalIcon } from 'icons/ArrowHorizontalIcon';
import { ProposalWantedBadgeIcon } from 'icons/badge/ProposalWantedBadgeIcon';
import { LockIcon } from 'icons/LockIcon';
import { MoveIcon } from 'icons/MoveIcon';
import { getTextLineStyle } from 'styles/common';
import { Button } from 'ui-components/common/Button';
import { EmptyList } from 'ui-components/common/EmptyList';
import { Link } from 'ui-components/common/Link';
import { ContentCover } from 'ui-components/contents/ContentCover';
import { ButtonSize } from 'constants/button';

const Empty = ({ onAddContent }: { onAddContent: () => void }) => (
  <StyledEmptyList title="작품이 없어요." description="픽앤츄에 도전해보세요!">
    <Button
      key="add"
      label="새 작품 등록하기"
      onClick={onAddContent}
      size={ButtonSize.Small}
      buttonTypography={Typography.XXXSmall}
    />
  </StyledEmptyList>
);

const PartnerRestricted = () => (
  <StyledEmptyList title="파트너 계정으로는 스튜디오를 사용할 수 없어요." description="다른 계정을 이용해 주세요." />
);

const getContentContestStatus = (stage: ContestStage, contentContest: ContentContestDto) => {
  if (stage === ContestStage.Writing) {
    return contentContest.stage === 1 ? '작성 중' : '접수 완료';
  }
  if (stage <= contentContest.stage) {
    if (stage === ContestStage.Accepted && contentContest.stage >= 3) {
      return '심사 중';
    }

    const statusMap = {
      [ContestStage.None]: '-',
      [ContestStage.Writing]: '작성 중',
      [ContestStage.Applied]: '검수 중',
      [ContestStage.Approved]: '투표 중',
      [ContestStage.Accepted]: '심사 중',
      [ContestStage.Announced]: '수상작 발표',
    };

    return statusMap[stage];
  }

  return '-';
};

export const MyContestContentList = ({
  contents,
  contestId,
  user,
  onAddContent,
}: {
  contestId: number;
  contents: ContentPublicRelayDto[];
  user?: UserMeDto;
  onAddContent: () => void;
}) => {
  const { tableContainerRef, showBoxShadow } = useTableHook();
  const { data: contentContestData } = useGetMyContestContentsQuery(contestId);
  const stage = useContestStage(contestId);
  const contentContestsMap = useMemo(
    () => (contentContestData ? toMap(contentContestData?.contentContests) : {}),
    [contentContestData],
  );
  const filteredContents = useMemo(() => {
    const contentContests = contentContestData?.contentContests;
    if (!contentContests) {
      return [];
    }
    return contents.filter(content => !!(content.contentContestId && contentContestsMap[content.contentContestId]));
  }, [contents, contentContestData, contentContestsMap]);

  return (
    <TableWrapper>
      <TableContainer ref={tableContainerRef}>
        <Table>
          <Thead showShadow={showBoxShadow.horizontal}>
            <tr>
              <StyledPinnedTh showShadow={showBoxShadow.vertical}>작품</StyledPinnedTh>
              <Th minWidth={80}>상태</Th>
              <Th minWidth={80}>회차 수</Th>
              <Th minWidth={80}>업데이트</Th>
              <Th minWidth={80}>조회</Th>
              <Th minWidth={80}>댓글</Th>
              <Th minWidth={80}>추천</Th>
              <Th minWidth={80}>작품 좋아요</Th>
              <Th minWidth={80}>알림</Th>
              <Th minWidth={80}>이멋공</Th>
              <Th minWidth={80}>투표수</Th>
              <Th minWidth={80}>작품 관리</Th>
              <Th minWidth={80}>게시된 작품</Th>
              <Th minWidth={80}>옵션</Th>
            </tr>
          </Thead>
          <Tbody>
            {filteredContents.map(content => (
              <Tr key={content.id}>
                <ThInTBody isPinned={showBoxShadow.vertical}>
                  <Link href={`/studio/contests/${contestId}/contents/${content.id}`}>
                    <StyledContentCoverMetaWrapper>
                      <StyledNewContentCover content={content} size={80} pcSize={90} />
                      <StyledContentMetaWrapper>
                        <StyledTitleWrapper>
                          {content.isProposalWanted && <ProposalWantedBadgeIcon />}
                          <StyledContentTitle>
                            {content.status === ContentStatus.Private ? (
                              <StyledLockIcon className="size-[14px]" />
                            ) : null}
                            {content.title}
                          </StyledContentTitle>
                        </StyledTitleWrapper>
                        <StyledContentDesc>{content.description}</StyledContentDesc>
                      </StyledContentMetaWrapper>
                    </StyledContentCoverMetaWrapper>
                  </Link>
                </ThInTBody>
                <Td>
                  <StyledTdWrapper>
                    <StyledContestStatus>
                      {content.contentContestId &&
                        getContentContestStatus(stage as ContestStage, contentContestsMap[content.contentContestId])}
                    </StyledContestStatus>
                  </StyledTdWrapper>
                </Td>
                <Td>
                  <StyledTdWrapper>{numberComma(content.counts.episodeCount)}</StyledTdWrapper>
                </Td>
                <Td>
                  <StyledTdWrapper>
                    {content.latestEpisodeOpenedAt
                      ? convertToDotString(content.latestEpisodeOpenedAt, true, true)
                      : '-'}
                  </StyledTdWrapper>
                </Td>
                <Td>
                  <StyledTdWrapper>{numberComma(content.counts.viewCount)}</StyledTdWrapper>
                </Td>
                <Td>
                  <StyledTdWrapper>{numberComma(content.counts.replyCount)}</StyledTdWrapper>
                </Td>
                <Td>
                  <StyledTdWrapper>{numberComma(content.counts.recommendCount)}</StyledTdWrapper>
                </Td>
                <Td>
                  <StyledTdWrapper>{numberComma(content.counts.favoriteCount)}</StyledTdWrapper>
                </Td>
                <Td>
                  <StyledTdWrapper>{numberComma(content.counts.newEpisodeNotiCount)}</StyledTdWrapper>
                </Td>
                <Td>
                  <StyledTdWrapper>{numberComma(content.counts.fancyShareCount)}</StyledTdWrapper>
                </Td>
                <Td>
                  <StyledTdWrapper>
                    {numberComma(/* fixme: @공모전접수 content.counts.contestVoteCount || */ 0)}
                  </StyledTdWrapper>
                </Td>
                <Td>
                  <StyledTdWrapper>
                    <Link href={`/studio/contests/${contestId}/contents/${content.id}`}>
                      <ArrowHorizontalIcon direction="right" color={Color.Black} size={20} />
                    </Link>
                  </StyledTdWrapper>
                </Td>
                <Td>
                  <StyledTdWrapper>
                    {content.status === ContentStatus.Private ? (
                      <MoveIcon color={Color.Black} size={20} opacity={0.3} />
                    ) : (
                      <Link href={`/contents/${content.id}`}>
                        <MoveIcon color={Color.Black} size={20} />
                      </Link>
                    )}
                  </StyledTdWrapper>
                </Td>
                <Td>
                  <StyledTdWrapper>
                    {content.contentContestId && contentContestsMap[content.contentContestId] && (
                      <ContestContentMoreMenu
                        contestId={contestId}
                        content={content}
                        contentContest={contentContestsMap[content.contentContestId]}
                      />
                    )}
                  </StyledTdWrapper>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </TableContainer>
      {user?.type === UserType.Partner && <PartnerRestricted />}
      {user?.type !== UserType.Partner && contents.length === 0 && <Empty onAddContent={onAddContent} />}
    </TableWrapper>
  );
};

const StyledTdWrapper = styled.div`
  width: 100%;
  height: 167px;
  padding: 16px;

  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  flex-grow: 0;
  white-space: pre-line;

  ${Typography.XXSmall};
  font-weight: ${FontWeight.Regular};
`;

const StyledContentCoverMetaWrapper = styled.div`
  width: 100%;
  height: 167px;
  padding: 0 16px;
  display: flex;
  gap: 16px;
  align-items: center;
`;

const StyledNewContentCover = styled(ContentCover)`
  flex-grow: 0;
  flex-shrink: 0;
`;

const StyledContentMetaWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  gap: 8px;
  flex-grow: 0;
  max-height: 135px;
`;

const StyledTitleWrapper = styled.div`
  text-align: left;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  gap: 4px;
`;

const StyledContentTitle = styled.div`
  text-align: left;

  ${Typography.XXSmall};
  font-weight: ${FontWeight.Medium};

  ${getTextLineStyle(2)};
`;

const StyledContentDesc = styled.div`
  text-align: left;

  color: ${Color.Gray600};

  ${Typography.XXXSmall};
  font-weight: ${FontWeight.Medium};

  ${getTextLineStyle(3)};
`;

const StyledEmptyList = styled(EmptyList)`
  margin: 200px 0 100px 0;
  position: relative;
  z-index: ${ZIndex.DirittoStudioEmptyList};
`;

const StyledPinnedTh = styled(PinnedTh)`
  width: 236px;

  @media (${isPC}) {
    width: 298px;
  }
`;

const StyledLockIcon = styled(LockIcon)`
  display: inline-block;
  vertical-align: middle;
  margin-right: 4px;
`;

const StyledContestStatus = styled.div`
  color: ${Color.Primary500};
`;
